 
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getWorkEntries, updateWorkEntry, deleteWorkEntry, exportWorkEntries, getEmployees, getProjects, downloadBlob } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ENTRY_STATUS, formatAmount, formatDate, formatDateTime, formatEur } from '../../lib/workStatus';
import { Check, Undo2, Pencil, Trash2, History, Download } from 'lucide-react';
import { toast } from 'sonner';

const now = new Date();
const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: new Date(2024, i, 1).toLocaleString('lv', { month: 'long' }) }));
const FIELD_LABELS = { amount: 'Apjoms', date: 'Datums', completed: 'Pabeigts', note: 'Piezīme', status: 'Statuss', admin_comment: 'Komentārs', task_id: 'Uzdevums' };

const HistoryList = ({ history }) => (
  <div className="space-y-1 text-xs" data-testid="entry-history">
    {history.length === 0 ? <p className="text-[#2E2E2E]/60">Izmaiņu nav.</p> : history.map((item, i) => (
      <div key={i} className="rounded-lg bg-[#f8faf8] px-3 py-2">
        <span className="font-medium">{FIELD_LABELS[item.field] || item.field}</span>: <span className="line-through text-[#2E2E2E]/50">{String(item.old_value ?? '-')}</span> → <span className="font-semibold">{String(item.new_value ?? '-')}</span>
        <span className="block text-[#2E2E2E]/50">{item.changed_by} · {formatDateTime(item.changed_at)}</span>
      </div>
    ))}
  </div>
);

const WorkEntriesManagement = () => {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ employee_id: 'all', status: 'submitted', project_id: 'all', year: String(now.getFullYear()), month: 'all' });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', amount: '', completed: false, note: '', admin_comment: '' });
  const [fixEntry, setFixEntry] = useState(null);
  const [fixComment, setFixComment] = useState('');
  const [historyEntry, setHistoryEntry] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (filters.employee_id !== 'all') p.employee_id = filters.employee_id;
    if (filters.status !== 'all') p.status = filters.status;
    if (filters.project_id !== 'all') p.project_id = filters.project_id;
    if (filters.year) p.year = Number(filters.year);
    if (filters.month !== 'all') p.month = Number(filters.month);
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [entryData, employeeData, projectData] = await Promise.all([getWorkEntries(params), getEmployees(), getProjects()]);
      setEntries(entryData || []); setEmployees(employeeData || []); setProjects(projectData || []);
    } catch (error) {
      toast.error('Neizdevās ielādēt atskaites');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (entry, status, comment) => {
    try {
      await updateWorkEntry(entry.id, { status, ...(comment !== undefined ? { admin_comment: comment } : {}) });
      toast.success(status === 'approved' ? 'Ieraksts apstiprināts' : status === 'needs_fix' ? 'Nosūtīts labošanai' : 'Statuss atjaunināts');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās atjaunināt');
    }
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({ date: entry.date.split('T')[0], amount: entry.amount, completed: entry.completed, note: entry.note || '', admin_comment: entry.admin_comment || '' });
  };

  const saveEdit = async () => {
    try {
      await updateWorkEntry(editing.id, { date: `${form.date}T00:00:00`, amount: Number(form.amount), completed: form.completed, note: form.note || null, admin_comment: form.admin_comment || null });
      toast.success('Ieraksts labots (izmaiņas saglabātas vēsturē)');
      setEditing(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Dzēst šo ierakstu?')) return;
    try {
      await deleteWorkEntry(entry.id);
      toast.success('Ieraksts dzēsts');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās dzēst');
    }
  };

  const exportXlsx = async () => {
    try {
      const blob = await exportWorkEntries(params);
      downloadBlob(blob, 'darbu_atskaite.xlsx');
    } catch (error) {
      toast.error('Neizdevās eksportēt');
    }
  };

  const totals = useMemo(() => ({
    count: entries.length,
    sum: entries.reduce((acc, e) => acc + (e.status !== 'needs_fix' ? e.line_total : 0), 0)
  }), [entries]);

  return (
    <div className="space-y-6" data-testid="admin-work-entries-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-work-entries-title">Darba atskaites</h1>
          <p className="text-[#2E2E2E]/70">Darbinieku iesniegtie darba ieraksti — pārbaudiet, labojiet, apstipriniet.</p>
        </div>
        <Button variant="outline" onClick={exportXlsx} data-testid="admin-work-entries-export"><Download className="w-4 h-4 mr-2" /> Eksportēt XLSX</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-work-entries-filters">
        <div className="space-y-1">
          <Label className="text-xs">Darbinieks</Label>
          <Select value={filters.employee_id} onValueChange={(v) => setFilters((p) => ({ ...p, employee_id: v }))}>
            <SelectTrigger data-testid="admin-work-entries-filter-employee"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Visi</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Statuss</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
            <SelectTrigger data-testid="admin-work-entries-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Visi</SelectItem>{Object.entries(ENTRY_STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`admin-work-entries-filter-status-${k}`}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Objekts</Label>
          <Select value={filters.project_id} onValueChange={(v) => setFilters((p) => ({ ...p, project_id: v }))}>
            <SelectTrigger data-testid="admin-work-entries-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Visi</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Gads</Label>
          <Select value={filters.year} onValueChange={(v) => setFilters((p) => ({ ...p, year: v }))}>
            <SelectTrigger data-testid="admin-work-entries-filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Mēnesis</Label>
          <Select value={filters.month} onValueChange={(v) => setFilters((p) => ({ ...p, month: v }))}>
            <SelectTrigger data-testid="admin-work-entries-filter-month"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Visi</SelectItem>{MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-6 py-3 border-b text-sm text-[#2E2E2E]/70">
            <span data-testid="admin-work-entries-count">{totals.count} ieraksti</span>
            <span data-testid="admin-work-entries-sum">Summa: <strong>{formatEur(totals.sum)}</strong></span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-40" data-testid="admin-work-entries-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-[#2E2E2E]/60" data-testid="admin-work-entries-empty">Nav ierakstu pēc izvēlētajiem filtriem.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f8faf8] text-left">
                  <tr>
                    <th className="px-4 py-3">Datums</th><th className="px-4 py-3">Darbinieks</th><th className="px-4 py-3">Objekts / uzdevums</th><th className="px-4 py-3">Apjoms</th><th className="px-4 py-3">Likme</th><th className="px-4 py-3">Summa</th><th className="px-4 py-3">Statuss</th><th className="px-4 py-3 text-right">Darbības</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => {
                    const st = ENTRY_STATUS[entry.status] || { label: entry.status, className: '' };
                    const locked = entry.status === 'closed';
                    return (
                      <tr key={entry.id} className="hover:bg-[#f8faf8]" data-testid={`work-entry-row-${entry.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">{formatDate(entry.date)}</td>
                        <td className="px-4 py-3">{entry.employee_name}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#1F3D2B]">{entry.task_title}</div>
                          <div className="text-xs text-[#2E2E2E]/60">{entry.project_name || '-'} · {entry.work_type_label || '-'}{entry.source === 'ai' ? ' · AI' : ''}</div>
                          {entry.note && <div className="text-xs text-[#2E2E2E]/60 italic">{entry.note}</div>}
                          {entry.admin_comment && <div className="text-xs text-amber-700">Komentārs: {entry.admin_comment}</div>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-testid={`work-entry-amount-${entry.id}`}>{formatAmount(entry.amount, entry.unit)}{entry.completed ? <span className="ml-1 text-green-700 text-xs">✓ pabeigts</span> : ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{formatEur(entry.rate_snapshot)}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-medium">{formatEur(entry.line_total)}</td>
                        <td className="px-4 py-3"><Badge className={st.className} data-testid={`work-entry-status-${entry.id}`}>{st.label}</Badge></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {entry.status !== 'approved' && !locked && (
                              <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => setStatus(entry, 'approved')} data-testid={`work-entry-approve-${entry.id}`}><Check className="w-4 h-4" /></Button>
                            )}
                            {entry.status !== 'needs_fix' && !locked && (
                              <Button size="sm" variant="outline" onClick={() => { setFixEntry(entry); setFixComment(entry.admin_comment || ''); }} title="Nosūtīt labošanai" data-testid={`work-entry-fix-${entry.id}`}><Undo2 className="w-4 h-4" /></Button>
                            )}
                            {!locked && <Button size="sm" variant="outline" onClick={() => openEdit(entry)} data-testid={`work-entry-edit-${entry.id}`}><Pencil className="w-4 h-4" /></Button>}
                            <Button size="sm" variant="ghost" onClick={() => setHistoryEntry(entry)} title="Izmaiņu vēsture" data-testid={`work-entry-history-${entry.id}`}><History className="w-4 h-4" />{entry.history?.length ? <span className="ml-1 text-xs">{entry.history.length}</span> : null}</Button>
                            {!locked && <Button size="sm" variant="ghost" onClick={() => remove(entry)} data-testid={`work-entry-delete-${entry.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="work-entry-edit-dialog">
          <DialogHeader><DialogTitle>Labot ierakstu</DialogTitle><DialogDescription>{editing?.employee_name} · {editing?.task_title}. Izmaiņas tiks saglabātas vēsturē.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Datums</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="work-entry-edit-date" /></div>
              <div className="space-y-1"><Label>Apjoms ({editing?.unit})</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="work-entry-edit-amount" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.completed} onCheckedChange={(v) => setForm((p) => ({ ...p, completed: Boolean(v) }))} data-testid="work-entry-edit-completed" /> Darbs pabeigts</label>
            <div className="space-y-1"><Label>Darbinieka piezīme</Label><Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="work-entry-edit-note" /></div>
            <div className="space-y-1"><Label>Administratora komentārs</Label><Textarea rows={2} value={form.admin_comment} onChange={(e) => setForm((p) => ({ ...p, admin_comment: e.target.value }))} data-testid="work-entry-edit-comment" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEditing(null)} data-testid="work-entry-edit-cancel">Atcelt</Button><Button onClick={saveEdit} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="work-entry-edit-save">Saglabāt</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fixEntry} onOpenChange={(open) => !open && setFixEntry(null)}>
        <DialogContent className="max-w-md" data-testid="work-entry-fix-dialog">
          <DialogHeader><DialogTitle>Nosūtīt labošanai</DialogTitle><DialogDescription>Darbinieks redzēs komentāru un varēs precizēt ierakstu.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={fixComment} onChange={(e) => setFixComment(e.target.value)} placeholder="Piem.: Vai dastošana 3,6 ha arī ir pabeigta?" data-testid="work-entry-fix-comment" />
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setFixEntry(null)} data-testid="work-entry-fix-cancel">Atcelt</Button><Button onClick={async () => { await setStatus(fixEntry, 'needs_fix', fixComment); setFixEntry(null); }} className="bg-amber-600 hover:bg-amber-700" data-testid="work-entry-fix-submit">Nosūtīt</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyEntry} onOpenChange={(open) => !open && setHistoryEntry(null)}>
        <DialogContent className="max-w-md" data-testid="work-entry-history-dialog">
          <DialogHeader><DialogTitle>Izmaiņu vēsture</DialogTitle><DialogDescription>{historyEntry?.task_title} · iesniegts {formatDateTime(historyEntry?.created_at)}</DialogDescription></DialogHeader>
          <HistoryList history={historyEntry?.history || []} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkEntriesManagement;
